// [uTheory](http://uTheory.com) - Copyright (c) Greg Ristow (2014)
//
// ## utils.js
//
// This file implements utility methods used by the rest of the uTheory
// codebase.
//

import { clone, cloneArray, cloneDeep } from './clone';
import { defaultsDeep } from './defaultsDeep';
import { merge } from './merge';
import { findKey } from './findKey';
import { get } from './get';
import { isFunction } from './isFunction';
import { isObject } from './isObject';
import { isString } from './isString';
import { tree } from './tree';
import uuid from './uuid';
import { isEqual } from './isEqual';
import { debounce } from './debounce';
import { isNumeric } from './isNumeric';
import { wait } from './wait';
import { isNumber } from './isNumber';
import { listToString } from './listToString';
import { arrayContentsEqual } from './arrayContentsEqual';
import { intersection, intersects } from './intersection';

const utils = {
  get uuid() {
    return uuid();
  },

  debounce,
  findKey,
  /**
   * Search an array or object by value, return the key of
   * the first item having that value
   * @param {any} needle - what to look for
   * @param {any[]|Object} - haystack where to look for it
   * @param {string} - [haystackProperty] property on each haystack object to compare
   * @returns {*} - found item or undefined if not found
   */
  arraySearch<T>(
    needle: any,
    haystack: T[] | { [key: string]: T },
    haystackProperty?: string | number
  ): string | number | undefined {
    if (Array.isArray(haystack)) {
      return haystack.findIndex((item) => {
        if (typeof haystackProperty !== 'undefined') {
          return (item as any)[haystackProperty] === needle;
        }
        return item === needle;
      });
    }

    const keys = Object.keys(haystack);
    const matchedKey = keys.find((key) => {
      const item = haystack[key];
      if (typeof haystackProperty !== 'undefined') {
        return (item as any)[haystackProperty] === needle;
      }
      return item === needle;
    });
    if (!matchedKey) return undefined;
    return matchedKey;
  },

  intersects,
  intersection,

  /**
   * Return an array filtered to include only unique elements
   * @param {array} array
   * @param {Function} equals function returns true if equal, false if not.
   * @returns {array} of unique elements
   */
  unique(array: any[], equals: Function = (a: any, b: any) => a === b): any[] {
    return array.filter(
      (el, i, arr) => arr.slice(0, i).find((el2) => equals(el, el2)) === undefined
    );
  },

  /**
   *
   */
  groupByAsObject<T extends Record<string, any>>(array: T[], key: string): { [key: string]: T[] } {
    const obj: { [key: string]: T[] } = {};
    this.groupBy(array, key).forEach((arr) => {
      obj[arr[0][key]] = arr;
    });
    return obj;
  },

  groupBy,

  /**
   * @param {Array} array
   * @param {number} n number of unique elements to return
   * @param {Function} comparator function to compare element equality
   * @returns {Array} array of unique elements n long (or as long as there are unique elements)
   */
  firstNUnique<T>(
    array: T[],
    n: number,
    comparator: (a: T, b: T) => boolean = (a: T, b: T) => a === b
  ): T[] {
    if (!utils.isArray(array)) {
      throw new Error('firstNUnique requires an array as its first argument.');
    }
    if (!utils.isInteger(n)) {
      throw new Error('firstNUnique requires an integer as its second argument.');
    }
    const newArr: T[] = [];
    const has = (newEl: T) => newArr.find((el) => comparator(newEl, el)) !== undefined;
    array.forEach((el) => {
      if (!has(el)) newArr.push(el);
    });
    return newArr.slice(0, Math.min(newArr.length, n));
  },

  /**
   * Take 'arr' and return a new list consisting of the sorted, unique
   * contents of arr. Does not modify 'arr'.
   * @param {Array} arr
   * @param {Function} cmp comparison function for sorting
   * @param {Function} eq equality checking function
   * @returns {Array}
   */
  sortAndUnique<T>(
    arr: T[],
    cmp: (a: any, b: any) => number = (a, b) => a - b,
    eq: (a: T, b: T) => boolean = (a: T, b: T) => a === b
  ): T[] {
    const newArr = [];
    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      let include = true;
      for (let j = 0; j < newArr.length; j++) {
        const newArrItem = newArr[j];
        if (eq(item, newArrItem)) {
          include = false;
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      if (include) newArr.push(item);
    }
    return newArr.sort(cmp);
  },

  // returns first matching item (not key) in array or object
  // use arraySearch to get the first matching key
  /**
   * returns first matching item (not key) in an array or object
   * use arraySearch to get the first matching key
   * @param {array | Object} arrayOrObject
   * @param {Function} callback return truthy if a match
   * @param {any?} thisArg
   * @returns {any}
   */
  find<T>(arrayOrObj: T[] | Record<string, T>, callback: Function, thisArg?: any): T | undefined {
    const call = thisArg ? callback.bind(thisArg) : callback;
    const key = Object.keys(arrayOrObj).find((i) => {
      const item = (arrayOrObj as Record<string, T>)[i];
      return call(item, i, arrayOrObj);
    });
    return key !== undefined ? (arrayOrObj as Record<string, T>)[key] : undefined;
  },

  /**
   * returns first matching index for an ARRAY ONLY
   * based on callback, returns -1 if not found.
   * @param {any[]} array
   * @param {Function} callback return truthy if a match
   * @param {any?} thisArg
   * @returns {number}
   */
  findIndex<T>(array: T[], callback: Function, thisArg?: any): number {
    const call = thisArg ? callback.bind(thisArg) : callback;
    let index = -1;
    for (let i = 0; i < array.length; i++) {
      if (call(array[i], i, array)) {
        index = i;
        break;
      }
    }
    return index;
  },

  closest,

  /**
   * Apply defaults deeply. When the default object has an array, if its matched by an array
   * on the specific object, then the first array element of the default object is used as
   * the defaults model, and applied to all elements that follow in the array.
   *
   * Only elements that appear on the defaults object are preserved -- all others are removed.
   * Used in uTheory API to remove elements that are not on a database schema, especially.
   * @param options
   * @param defaults
   */
  defaultsDeep,

  /**
   * pluck a single property from an array of objects.
   * @param {Object[]} objects
   * @param {string} property
   * @returns {array}
   */
  pluck(objects: object[], property: string): any[] {
    return objects.map((obj) => utils.plucker(obj, property));
  },

  /**
   * helper function to pluck a deep prop from an object.
   * @param {Object|array} object
   * @param {string?} prop such as a.b.c
   * @returns {any} value of object[prop], or undefined.
   */
  plucker: get,

  /**
   * helper function to pluck a deep prop from an object.
   * @param {Object|array} object
   * @param {string?} prop such as a.b.c
   * @returns {any} value of object[prop], or undefined.
   */
  get,

  /**
   * Set a value (including nested) on an object
   * @param {Object} object
   * @param {string | boolean} prop including prop.subprop
   * @param {any} value to set the prop
   */
  set(object: object, prop: string | boolean = false, value: any) {
    if (prop === false) return object;
    if (typeof prop !== 'string') return object;
    const props = prop.split('.');
    if (!props.length) return object;
    if (props.length === 1) {
      (object as any)[prop] = value;
    } else {
      const parentObjPropName = props.slice(0, -1).join('.');
      const parentObj = utils.plucker(object, parentObjPropName);
      if (!utils.isObject(parentObj) && !utils.isArray(parentObj)) {
        throw new Error(`${parentObjPropName} is not an object.`);
      }
      (parentObj as any)[props.pop() as string] = value;
    }
    return object;
  },

  /**
   * sort(array, lambaOrProp1[, lambaOrProp2, etc...])
   * @param {Array} array
   * @param {Function|string} lambdaOrProp if a non-function is provided for lambda
   * (string, numeric, etc...) it is assumed to be a key on the objects.
   * in lambdas, return:
   *   -1 to keep a first in array
   *   1 to swap b ahead of a in array
   *   0 to use next sort
   * Props should be a string format: if they start with a minus sign, they will sort
   * in reverse order.
   * @param {...Function[]|string[]} lambdasOrProps
   * @returns {Array} sorted array
   * Sorts by lambda1, then lambda2, etc...
   */
  sort<T>(
    array: T[],
    lambdaOrProp: ((a: T, b: T) => number) | string,
    ...lambdasOrProps: (((a: T, b: T) => number) | string)[]
  ): T[] {
    type LambdaOrProp = ((a: T, b: T) => number) | string;
    const recursiveSort = (
      a: T,
      b: T,
      thisLambdaOrProp: LambdaOrProp,
      ...remainingLambdaOrProps: LambdaOrProp[]
    ): number => {
      let swap;
      let reverse = false;
      if (!utils.isFunction(thisLambdaOrProp)) {
        let prop = thisLambdaOrProp;
        if (utils.isString(prop) && prop[0] === '-') {
          prop = prop.slice(1);
          reverse = true;
        }
        let propOnA = utils.plucker(a as any, prop);
        let propOnB = utils.plucker(b as any, prop);
        if (utils.isString(propOnA) && utils.isString(propOnB)) {
          propOnA = propOnA.toLowerCase();
          propOnB = propOnB.toLowerCase();
        }
        swap = propOnA < propOnB ? -1 : propOnA > propOnB ? 1 : 0;
      } else {
        const lambda = thisLambdaOrProp;
        swap = lambda(a, b);
      }
      if (swap === 0 && remainingLambdaOrProps.length) {
        return recursiveSort(a, b, remainingLambdaOrProps[0], ...remainingLambdaOrProps.slice(1));
      }
      return swap * (reverse ? -1 : 1);
    };
    return array.sort((a, b) => recursiveSort(a, b, lambdaOrProp, ...lambdasOrProps));
  },

  /**
   * Are two arrays identical?
   * @param {any[]} array1
   * @param {any[]} array2
   * @returns {boolean}
   */
  arrayCompare(array1: any[], array2: any[]): boolean {
    if (array1.length !== array2.length) return false;
    for (let i = 0; i < array1.length; i++) {
      if (array1[i] !== array2[i]) return false;
    }
    return true;
  },

  /**
   * Are contents of two arrays identical, ignoring order and repetitions?
   * @param array1
   * @param array2
   * @param equals (a, b) => a === b
   */
  arrayContentsEqual,

  /**
   * @param {Array} array of nested arrays of any depth.
   * @param {boolean} deep = true. If true, flatten only one level.
   * @returns {Array} flat array
   */
  flatten(array: any[], deep = true): any[] {
    return array.flat(deep ? Infinity : 1);
  },

  /**
   * Are two sets equal?
   * @param {Set<any>} set1
   * @param {Set<any>} set2
   * @returns {boolean}
   */
  setCompare(set1: Set<any>, set2: Set<any>): boolean {
    if (set1.size !== set2.size) return false;
    const arr = Array.from(set1);
    const notShared = arr.find((el) => !set2.has(el));
    if (typeof notShared !== 'undefined') return false;
    return true;
  },

  /**
   * Check equality of array or object, shallow by default!
   * @param {Object|Array} object1
   * @param {Object|Array} object2
   * @param {boolean} [deep = false]
   */
  equals: (object1: object | any[], object2: object | any[], deep = false) =>
    isEqual(object1, object2, deep),

  /**
   * Generates a random integer within a specified range
   * @param options
   * @param {number} [options.min] default 0
   * @param {number} [options.max]
   */
  randomInteger({ min = 0, max }: { min?: number; max: number }) {
    if (!utils.isNumber(max)) {
      throw new Error(`randomNumber requires that max be a number, not ${utils.typeof(max)}`);
    }
    if (!utils.isNumber(min)) {
      throw new Error(`randomNumber requires that min be a number, not ${utils.typeof(min)}`);
    }

    min = Math.floor(min);
    max = Math.floor(max);
    if (min > max) {
      const swap = max;
      max = min;
      min = swap;
    }
    return Math.floor(Math.random() * (max - min)) + min;
  },

  /**
   * Returns a random element from an array, leaving the array unmodified.
   * @param array
   */
  randomElement<T>(array: T[]): T {
    const index = utils.randomInteger({ min: 0, max: array.length - 1 });
    return array[index];
  },

  /**
   * In-place shuffle of an array.
   * @param {Array} array
   * @returns {Array} in-place! Original is modified.
   */
  shuffle<T>(array: T[]): T[] {
    if (array.length === 1) return array;
    if (utils.unique(array).length === 1) return array;
    let currentIndex = array.length;
    let temporaryValue;
    let randomIndex;

    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      // eslint-disable-next-line no-param-reassign
      array[currentIndex] = array[randomIndex];
      // eslint-disable-next-line no-param-reassign
      array[randomIndex] = temporaryValue;
    }

    return array;
  },

  /**
   * Re-sort an array to minimize the number of adjacent unique elements, and
   * give a fairly even distribution -- as for randomizing question order.
   * @param {array} arr an array of unsorted elements
   * @param {Function|string} equalsOrParam a comparator function or string name of the parameter
   * @returns {Array} an array of elements with as few adjacent uniques as possible.
   */
  avoidAdjacentUniques<T>(
    array: T[],
    equalsOrParam: ((a: T, b: T) => boolean) | string = (a, b) => a === b
  ): T[] {
    const comparator =
      typeof equalsOrParam === 'string'
        ? (a: any, b: any) => a[equalsOrParam] === b[equalsOrParam]
        : equalsOrParam;

    // shuffle for entropy
    array = utils.shuffle(array.slice(0));

    // group into buckets of like kind (ones that cannot be next to each other),
    // place biggest bucket at head of queue.
    const buckets: T[][] = utils.groupBy(array, comparator).sort((a, b) => b.length - a.length);

    // make array of available indexes
    const availableIndexes = Array.from(array.keys());
    const distributedArray: T[] = [];

    // for each bucket, from largest to smallest, spread the members out evenly among
    // the available indexes
    buckets.forEach((bucket) => {
      const skipThisManyIndexes = Math.floor(availableIndexes.length / bucket.length);
      let remainder = availableIndexes.length % bucket.length;
      let usedRemainder = 0;
      const lastAvailableIndex = availableIndexes.length - 1;

      bucket.forEach((element, i) => {
        let indexOfIndexes = lastAvailableIndex - i * skipThisManyIndexes - usedRemainder;
        if (i > 0 && remainder) {
          remainder--;
          usedRemainder++;
          indexOfIndexes--;
        }

        const index = availableIndexes[indexOfIndexes];
        availableIndexes.splice(indexOfIndexes, 1);
        distributedArray[index] = element;
      });
    });

    // explore several random single-pair swaps to increase entropy
    const indexes = Array.from(distributedArray.keys());
    indexes.forEach(shuffleFromIndexes);
    const shuffledIndexes = utils.shuffle(Array.from(distributedArray.keys()));
    shuffledIndexes.forEach(shuffleFromIndexes);

    function shuffleFromIndexes(index1: number, indexsIndex: number) {
      if (indexsIndex + 1 >= indexes.length) return;
      const index2 = indexes[indexsIndex + 1];
      if (swapIsOk(distributedArray, index1, index2) && Math.random() > 0.5) {
        swap(distributedArray, index1, index2);
      }
    }

    return distributedArray;
    function swapIsOk(arrayToSwapOn: T[], index1: number, index2: number) {
      swap(arrayToSwapOn, index1, index2);
      const swapWasOk = isOk(arrayToSwapOn, index1) && isOk(arrayToSwapOn, index2);
      swap(arrayToSwapOn, index1, index2);
      return swapWasOk;
    }

    function swap(arrayToSwapOn: T[], index1: number, index2: number) {
      const swapEl = arrayToSwapOn[index1];
      arrayToSwapOn[index1] = arrayToSwapOn[index2];
      arrayToSwapOn[index2] = swapEl;
    }

    function isOk(arrayThatWasSwapped: T[], index: number) {
      const element = arrayThatWasSwapped[index];
      const priorOk = index - 1 < 0 || !comparator(element, arrayThatWasSwapped[index - 1]);
      const nextOk =
        index + 1 >= arrayThatWasSwapped.length ||
        !comparator(element, arrayThatWasSwapped[index + 1]);
      return priorOk && nextOk;
    }
  },

  // tslint:disable-next-line:function-name
  RERR(code: string, message: string) {
    const log = `${code}: ${message}`;
    throw new Error(log);
  },

  typeError(expected: string, got: any) {
    const message = `Invalid type.  Expected type ${expected}. Instead got type ${
      got.constructor.name
    } / typeof: ${typeof got}`;
    const code = 'Type';
    utils.RERR(code, message);
  },

  tree,

  /**
   * Merge two objects, deep by default.
   */
  merge,

  /**
   * Clone an array or object
   * @param {Object | array} source
   * @param {boolean=} deep
   */
  clone,

  cloneDeep,

  /**
   * @param {array} array
   * @param {boolean=} deep
   * @returns {array}
   */
  cloneArray,

  /**
   * Return the type of any element: object, string, null, undefined, array, function, boolean, number, NaN.
   * @param {any} o
   * @returns {string} object, string, null, undefined, array, function, boolean, number, NaN.
   *
   */
  typeof(o: any): string {
    if (utils.isObject(o)) return 'object';
    if (utils.isString(o)) return 'string';
    if (o === null) return 'null';
    if (typeof o === 'undefined') return 'undefined';
    if (utils.isArray(o)) return 'array';
    if (utils.isFunction(o)) return 'function';
    if (typeof o === 'boolean') return 'boolean';
    if (Number.isNaN(Number(o))) return 'NaN';
    if (!Number.isNaN(Number(o)) && Number.isFinite(Number(o))) return 'number';
    return typeof o;
  },

  isUndefined: (o: any): boolean => typeof o === 'undefined',

  isEmptyObject: (o: any): boolean => {
    if (typeof o !== 'object') return false;
    return Object.keys(o).length === 0;
  },

  isArray: (o: any): o is any[] => Object.prototype.toString.call(o) === '[object Array]',

  isString,

  isNumber,

  isNumeric,

  isInteger: (n: any): boolean => typeof n === 'number' && n % 1 === 0,

  isObject,

  isFunction,

  isNull: (f: any): f is null => f === null,

  /**
   * Convert likely string negative types to boolean, e.g., "no" => false; "yes" => true
   * @param n a string, boolean, etc...
   */
  toBoolean(n: any) {
    if (utils.isString(n)) {
      if ([0, '0', 'false', 'null', 'undefined', 'no', 'n'].indexOf(n.toLowerCase()) !== -1) {
        return false;
      }
      return true;
    }
    return !!n;
  },

  /**
   * Convert array-like string to an array
   * @param str in format "[3, 2, '12']"
   * @param forceIt if not an array string, turn it into [string]
   */
  toArray(str: string | any[], forceIt: boolean = false) {
    if (Array.isArray(str)) return str;

    if (utils.isString(str) && str[str.length - 1] === ']' && str[0] === '[') {
      const s = JSON.parse(str);
      return s;
    }
    return forceIt ? [str] : str;
  },

  /**
   * Convert a key/value map to a url query parameter such as ?likes-fish=true&peanut-allergy=false
   * @param data { key: value }
   */
  toQuery(data: Record<string, any>) {
    if (typeof data !== 'object') return '';

    let queryString = '';
    let connector = '?';
    Object.keys(data).forEach((prop) => {
      queryString += `${connector + encodeURIComponent(prop)}=${encodeURIComponent(data[prop])}`;
      connector = '&';
    });
    return queryString;
  },

  /**
   * @param {Object|Array} obj
   * @param {Function} callback
   * @param {any?} thisArg
   */
  forEach(obj: object | any[], callback: Function, thisArg?: any) {
    Object.keys(obj).forEach((key) => {
      const item = (obj as any)[key];
      callback.call(thisArg || this, item, key, obj);
    });
  },

  /**
   * For each on a NodeList... this will eventually be unnecessary when NodeList.prototype.forEach hits all browsers
   * @param {NodeList} nodeList
   * @param {Function} callback
   * @param {any} thisArg
   */
  nodeListForEach<T extends Element>(
    nodeList: NodeListOf<T> | HTMLCollectionOf<T>,
    callback: (node: T, index: number, nodeArray: T[]) => any,
    thisArg?: any
  ) {
    callback = thisArg ? callback.bind(thisArg) : callback;
    const nodeArray = Array.from(nodeList);
    nodeArray.forEach((node, index, arr) => callback(node, index, arr));
  },
  /**
   * Quick utility to use Map on an object:
   * passes (value, key, obj) instead of (value, index, arr) to callback
   * GCR: Consider re-writing to NOT overwrite initial obj, but instead
   * return a new obj, as array.map would.
   * @param {Object} obj
   * @param {Function} callback
   * @param {any} thisArg
   * @return {Object} original object w/ overwritten props from map
   */
  map(obj: object, callback: Function, thisArg?: any): object {
    const mapper = thisArg
      ? (key: string) => callback.call(thisArg, (obj as any)[key], key, obj)
      : (key: string) => callback((obj as any)[key], key, obj);
    Object.keys(obj).forEach((key) => {
      // eslint-disable-next-line no-param-reassign
      (obj as any)[key] = mapper(key);
    });
    return obj;
  },

  /**
   * Maps over an object, filtering out properties that match a callback, returning a new
   * object containing only those properties that matched the case of the callback.
   * @param {Object} obj
   * @param {Function} callback (value, key, originalObject) => boolean
   * @returns {Object} with only props matching callback included
   */
  filter(obj: object, callback: Function, thisArg = false): object {
    callback = thisArg ? callback.bind(thisArg) : callback;
    const newObj = {};
    Object.keys(obj).forEach((key) => {
      if (callback((obj as any)[key], key, obj)) (newObj as any)[key] = (obj as any)[key];
    });
    return obj;
  },

  /**
   * mapToArray converts an object to an array. If no function is given, the array
   * will consist of the values of each object property.
   * @param {Object} obj
   * @param {Function} cb = (el, key) => obj[key];
   * @returns {array}
   */
  mapToArray(obj: object, cb: Function = (el: any) => el): any[] {
    return Object.keys(obj).map((key) => cb((obj as any)[key], key));
  },

  /**
   * basic prototypal inheritance model
   * DO NOT REMOVE! THIS IS USED HEAVILY IN UTHEORY-API
   * @param child Child
   * @param parent Parent
   * @param obj object to extend C with
   */
  /* eslint-disable func-names */
  Inherit: (function () {
    const F = function () {};
    // `C` is Child. `P` is parent. `O` is an object to
    // to extend `C` with.
    return function (child: Function, parent: Function, obj: object) {
      F.prototype = parent.prototype;
      child.prototype = new (F as any)();
      (child as any).superclass = parent.prototype;
      child.prototype.constructor = child;
      utils.merge(child.prototype, obj);
      return child;
    };
  })(),
  /* eslint-enable func-names */

  // Is every element of arr1 present in arr2?
  /**
   * @param {array.<any>} needle
   * @param {array.<any>} haystack
   * @param {Function} callback
   * @param {any?} thisArg
   * @returns {boolean}
   */
  arrayInArray<T>(needle: T[], haystack: T[], callback?: Function, thisArg?: any): boolean {
    if (callback) {
      return needle.every((el) =>
        thisArg ? callback.call(thisArg, el, haystack) !== -1 : haystack.indexOf(el) !== -1
      );
    }
    return needle.every((el) =>
      // @ts-ignore callback is clearly not undefined by this point.
      thisArg ? (callback as Function)(el, haystack) !== -1 : haystack.indexOf(el) !== -1
    );
  },

  /**
   * b-search through numbers to find the closest match
   * @param {number} min minimum possible value
   * @param {number} max maximum possible value
   * @param {Function} test lambda returns 1 if too big, -1 if too small, 0 if good.
   * @param {number} step default 1
   * @param {number} maxIterations max slices to go through -- default 20 (finds in 1 Million),
   * @returns {number}
   * prevents infinite loops if test fails.
   */
  binaryNumberSearch(
    min: number,
    max: number,
    test: Function,
    step = 1,
    maxIterations = 20
  ): number {
    let iteration = 0;
    let startIndex = min;
    let stopIndex = max;
    // Binary shift one to right, discarding shifted bits
    // magically gives us midpoint:
    let index = (((startIndex + stopIndex) / step) >> 1) * step;
    let match = test(index);
    while (match !== 0 && ++iteration < maxIterations && startIndex < stopIndex) {
      // If found value too big
      if (match > 0) {
        stopIndex = index - step;
      } else if (match < 0) {
        startIndex = index + step;
      }
      // Recenter
      index = (((startIndex + stopIndex) / step) >> 1) * step;
      match = test(index);
    }
    return index;
  },

  /**
   * Return a function that keeps a running average
   * @returns {Function} (newAverage, newQs) => average
   */
  averageFactory(): (newAverage: number, newQs?: number) => number {
    let priorQs = 0;
    let average = 0;
    return function averager(newAverage: number, newQs = 1): number {
      average = utils.newAverage(average, priorQs, newAverage, newQs);
      priorQs += newQs;
      return average;
    };
  },

  // Running average function:
  /**
   * Get running average based on prior average & number of questions
   * @param {number} oldAverage
   * @param {number} priorQs
   * @param {number} newAverage
   * @param {number=} newQs
   * @returns {number} new average
   */
  newAverage(
    oldAverage: number,
    priorQs: number,
    newAverage: number,
    newQs: number | undefined = 1
  ): number {
    if (!utils.isNumeric(oldAverage)) {
      throw new Error(
        `oldAverage must be a number, not ${utils.typeof(oldAverage)}. Value: ${oldAverage}`
      );
    }
    if (!utils.isNumeric(newAverage)) {
      throw new Error(
        `newAverage must be a number, not ${utils.typeof(newAverage)}. Value: ${newAverage}`
      );
    }
    if (!utils.isNumeric(priorQs)) {
      throw new Error(`priorQs must be a number, not ${utils.typeof(priorQs)}. Value: ${priorQs}`);
    }
    if (!utils.isNumeric(newQs)) {
      throw new Error(`newQs must be a number, not ${utils.typeof(newQs)}. Value: ${newQs}`);
    }
    return (oldAverage * priorQs + newAverage * newQs) / (priorQs + newQs);
  },

  /**
   * Round number x to nearest fractional value n (.5, .25, etc..);
   * @param {number} x
   * @param {number} n optional defaults 1
   * @returns {number}
   */
  roundN(x: number, n = 1): number {
    return Math.round(x / n) * n;
  },

  // Convert an integer, like 1, to ordinal, like 1st.
  /**
   * Convert an integer, like 1, to ordinal, like 1st.
   * @param {number} x
   * @returns {string} 1st, 2nd, 3rd, etc...
   */
  ordinal(x: number): string {
    return x + (~~(x / 10) % 10 === 1 ? 'th' : ['', 'st', 'nd', 'rd'][x % 10] || 'th');
  },

  /**
   * Make the first letter of a string upper case
   * @param text
   */
  titleCase(text: string): string {
    return text.slice(0, 1).toUpperCase() + text.slice(1).toLowerCase();
  },

  /**
   * Converts a number to a tuple ('double', 'triple', etc...)
   * @param x number to be converted to a tuple
   */
  tuple(x: number, upperCase: boolean = false): string {
    if (upperCase) return utils.titleCase(utils.tuple(x));
    switch (x) {
      case 1:
        return 'single';
      case 2:
        return 'double';
      case 3:
        return 'triple';
      case 4:
        return 'quadruple';
      case 5:
        return 'quintuple';
      case 6:
        return 'sextuple';
      case 7:
        return 'septuple';
      case 8:
        return 'octuple';
      case 9:
        return 'nonuple';
      case 10:
        return 'dectuple';
      default:
        return `${x}-tuple`;
    }
  },

  listToString,

  // Is this a touch device?
  /**
   * Is this device a touch device?
   * @returns {boolean}
   */
  isTouch(): boolean {
    return 'ontouchstart' in document.documentElement;
  },
  /**
   * return "a" or "an"
   * not thoroughly tested! -- written by GCR...
   * will fail on silent h words like "honor"
   * @param {string|number} text
   * @returns {string}
   */
  aOrAn(x: string | number): string {
    if (utils.isNumeric(x) && (x === 11 || x === 18)) return 'an';
    const y = `${x}`.toLowerCase();
    if (y === '11' || y === '18') return 'an';
    return ['a', 'e', 'i', 'o', 'u', '8'].indexOf(y[0]) + 1 ? 'an' : 'a';
  },

  /**
   * Benchmark. Run function `f` once and report time elapsed shifted by `s` milliseconds.
   * @param {number} s in milliseconds
   * @param {Function} f
   * @returns {number} number of milliseconds to run function
   */
  benchmark(s: number, f: Function): number {
    const startTime = new Date().getTime();
    f();
    const elapsed = new Date().getTime() - startTime;
    console.log(`${s + elapsed}ms`);
    return elapsed;
  },

  /**
   * Returns a promise that resolves after specified seconds
   * @param {number} seconds
   * @returns {Promise}
   */
  wait,

  // Add Class to doc:
  /**
   * @param {string} name of class WITHOUT a dot
   * @param {string} rules the part that would be within { } in a CSS file.
   */
  createClass(name: string, rules: string) {
    if (typeof document === 'undefined') return;
    const className = `.${name}`;
    const style: HTMLStyleElement = document.createElement('style');
    style.type = 'text/css';
    document.getElementsByTagName('head')[0].appendChild(style);
    const sheet: CSSStyleSheet = style.sheet || (style as any).styleSheet;
    if (!sheet.insertRule && sheet.addRule) {
      sheet.addRule(name, rules);
    } else if (sheet.insertRule) {
      sheet.insertRule(`${className}{${rules}}`, 0);
    }
  },

  /**
   * Convert a CSSStyleDeclaration to a basic key:value object.
   * @param {CSSStyleDeclaration} style
   * @returns {object}
   */
  // tslint:disable-next-line:function-name
  CSSStyleDeclarationToObject(style: CSSStyleDeclaration): object {
    const length = style.length;
    const styleObj: Record<string, string> = {};
    for (let i = 0; i < length; i++) {
      const prop = style.item(i);
      styleObj[prop] = style.getPropertyValue(prop);
    }
    return styleObj;
  },

  /**
   * Measure text height & width,
   * @param {Object} options
   * @param {string} options.text
   * @param {HTMLDivElement} options.div
   * @param {number?} options.width if limited in pixels
   * @param {height?} options.height if limited in pixels
   * @param {boolean} resizeOriginal should the original be resized?
   * @returns {number} fontSize
   */
  fitText(
    {
      text,
      div,
      width,
      height,
    }: { text: string; div: HTMLDivElement; width?: number; height?: number },
    resizeOriginal?: boolean
  ) {
    // const oldStyles = utils.merge({}, options.div.style);
    const oldStyles = utils.CSSStyleDeclarationToObject(div.style);
    if (!width && !height) return 12;

    const measureDiv = div;
    measureDiv.style.width = 'auto';
    measureDiv.style.height = 'auto';
    measureDiv.style.maxWidth = '';
    measureDiv.style.display = 'inline-block';
    if (text) measureDiv.innerHTML = text;
    //  document.body.appendChild(measureDiv);
    //  console.log("measureDiv.style:");
    //  console.log(measureDiv.style);
    // 1 if too big; -1 if too small; 0 if good.
    const fits = function fits() {
      let heightFits = height ? measureDiv.clientHeight <= height : true;
      let widthFits = width ? measureDiv.clientWidth <= width : true;
      // If the height fits, but the width doesn't, try word wrapping it...
      if (height && heightFits && !widthFits) {
        measureDiv.style.maxWidth = `${width}px`;
        measureDiv.style.overflow = 'hidden';
        if (measureDiv.scrollWidth <= measureDiv.clientWidth) {
          heightFits = measureDiv.scrollHeight <= height;
          widthFits = true;
        }
        measureDiv.style.maxWidth = '';
        measureDiv.style.maxHeight = '';
      }
      return heightFits && widthFits;
    };

    // start at a size that's probably too small:
    let searchLow = 1;
    let searchHigh = 200;
    let searchPoint;
    let fontSize = 12;
    let doneSearching = false;
    while (!doneSearching) {
      // searchPoint = middle of searchLow & searchHigh
      searchPoint = searchLow + (searchHigh - searchLow) / 2;
      measureDiv.style.fontSize = `${searchPoint}px`;
      if (fits()) {
        // If it's too small/fits: searchLow = searchPoint
        searchLow = searchPoint;
      } else {
        // if it's too big, searchHigh = searchPoint
        searchHigh = searchPoint;
      }
      // If the search distance is smaller than __px,
      // be done with it.
      // fontSize =  searchPoint, if in range, or searchLow.
      if (searchHigh - searchLow < 2) {
        fontSize = fits() ? searchPoint : searchLow;
        measureDiv.style.fontSize = `${fontSize}px`;
        doneSearching = true;
      }
    }
    //  document.body.removeChild(measureDiv);
    utils.merge(div.style, oldStyles);
    // eslint-disable-next-line no-param-reassign
    if (resizeOriginal) div.style.fontSize = `${fontSize}px`;
    return fontSize;
  },

  /**
   * Encore an options object as parameters in a query object. Returns ?width=234&height=126 type string.
   * @param {Object} params
   * @returns {string}
   */
  uriEncode(params: Record<string, string>): string {
    let str = '';
    Object.keys(params).forEach((key) => {
      if (str !== '') {
        str += '&';
      } else {
        str = '?';
      }
      str += `${key}=${encodeURIComponent(params[key])}`;
    });
    return str;
  },
};

export default utils;

/**
 * find the closest match in an array, return its index (not value!)
 * @param {any} needle
 * @param {array} haystack
 * @param {Function} [callback] evaluates as closest number if omitted
 * @param {Object} [thisArg]
 * @return {number} index
 */
function closest<T>(
  needle: any,
  haystack: T[] | { [key: string]: T },
  callback?: Function,
  thisArg?: object
): number | string | undefined {
  let closestKey;
  let distance: number;
  let compare = (a: any, b: any) => Math.abs(a - b);
  if (callback) {
    compare = thisArg ? callback.bind(thisArg) : callback;
  }
  Object.keys(haystack).forEach((key) => {
    const item: T = (haystack as any)[key];
    const dist = compare(needle, item);
    if (typeof distance === 'undefined') {
      distance = dist;
      closestKey = key;
    } else if (dist < distance) {
      //      console.log(`distance ${dist} smaller than ${distance}`);
      //      console.log(`New index: ${key}`);
      closestKey = key;
      distance = dist;
    }
  });
  if (utils.isArray(haystack)) closestKey = Number(closestKey);
  return closestKey;
}

/**
 * Groups an array by matching parameters, or per a callback
 * @param {array} arr the arr to group
 * @param {(string|Function)} paramOrEqualityFunction el[param], or equality checking function.
 *    Defaults to === for grouping.
 * @returns {array[]} array of arrays by grouping.
 */
function groupBy<T extends Record<string, any>>(
  arr: T[],
  paramOrEqualityFunction?: string | ((a: any, b: any) => boolean)
): T[][];
function groupBy<T>(
  arr: T[],
  paramOrEqualityFunction?: string | ((a: any, b: any) => boolean)
): T[][];
function groupBy<T extends Record<string, any>>(
  arr: T[],
  paramOrEqualityFunction?: string | ((a: any, b: any) => boolean)
): T[][] {
  let equals = (a: any, b: any) => a === b;
  if (utils.isString(paramOrEqualityFunction)) {
    equals = (a, b) => a[paramOrEqualityFunction] === b[paramOrEqualityFunction];
  } else if (typeof paramOrEqualityFunction === 'function') {
    equals = paramOrEqualityFunction;
  }

  const groupedArr: T[][] = [];
  const addIfMatch = (el: T) =>
    groupedArr.find((group) => {
      const eq = equals(group[0], el);
      if (eq) group.push(el);
      return eq;
    });
  arr.forEach((el) => {
    if (!addIfMatch(el)) {
      groupedArr.push([el]);
    }
  });
  return groupedArr;
}
